import styled, { css } from "styled-components";
import type { DefaultTheme } from "styled-components";
import { lineHeights, spacing as uiSpacing } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { mediaQueries } from "../../../design-system/tokens/breakpoints";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import type {
  DataTableAlign,
  DataTableBodyVariant,
  DataTableDensity,
  DataTableEmphasis,
  DataTableHeadAppearance,
} from "./DataTable.types";

/** Hides a cell under the `md` breakpoint (header and body cells of a `hideOnNarrow` column). */
const narrowHidden = css`
  ${mediaQueries.down.sm} {
    display: none;
  }
`;

const textAlignByMode: Record<DataTableAlign, string> = {
  left: "left",
  center: "center",
  right: "right",
};

/** Map a semantic emphasis level to a theme text colour. */
const emphasisToColor = (
  emphasis: DataTableEmphasis,
  theme: DefaultTheme,
): string => {
  switch (emphasis) {
    case "high":
      return theme.colors.text.primary;
    case "low":
      return theme.colors.text.tertiary;
    case "interactive":
      return theme.colors.text.interactive;
    case "medium":
    default:
      return theme.colors.text.secondary;
  }
};

/** Compact rows (design F13): 40px header and body rows, 16px side padding. GAP: the design's 38px rows have no spacing token. */
const compactHead = css`
  height: ${spacing[10]};
  padding: 0 ${spacing[4]};
  background-color: ${rawColors.grey[50]};
  color: ${rawColors.text.mediumEmphasis};
  text-transform: none;
  ${typographyMixin("caption", 1)}
`;

const compactCell = css`
  padding: 0 ${spacing[4]};
  font-variant-numeric: tabular-nums;
  /* GAP: the design's 13px has no type token; body 4 (14px) is the nearest. */
  ${typographyMixin("body", 4)}
`;

export const TableWrap = styled.div<{ $chromeless?: boolean }>`
  background-color: ${({ theme }) => theme.colors.background.primary};
  overflow: visible;

  ${({ $chromeless }) =>
    !$chromeless &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.border.stroke};
      border-radius: 10px;
    `}
`;

export const Table = styled.table<{
  $stickyHeader?: boolean;
  $layoutFixed?: boolean;
}>`
  width: 100%;
  ${({ $layoutFixed }) =>
    $layoutFixed &&
    css`
      table-layout: fixed;
    `}
  border-collapse: ${({ $stickyHeader }) =>
    $stickyHeader ? "separate" : "collapse"};
  border-spacing: ${({ $stickyHeader }) => ($stickyHeader ? "0" : "initial")};
`;

/**
 * Sticky on `<thead>` (not each `<th>`) keeps one composited band — avoids the
 * tiny column-to-column “stretch” jitter seen when every header cell sticks alone.
 */
export const TableHead = styled.thead<{ $sticky?: boolean; $plain?: boolean; $density?: DataTableDensity }>`
  ${({ $sticky, $plain, $density, theme }) =>
    $sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 3;
      background-color: ${$plain
        ? theme.colors.surface.primary
        : $density === "compact"
          ? rawColors.grey[50]
          : theme.colors.primary[100]};
      box-shadow: 0 1px 0 0 ${$plain ? theme.colors.border.subtle : theme.colors.border.stroke};
    `}
`;

export const HeadCell = styled.th<{
  $align: DataTableAlign;
  $width?: string;
  $stickyHeader?: boolean;
  $hideNarrow?: boolean;
  $appearance?: DataTableHeadAppearance;
  $density?: DataTableDensity;
}>`
  ${({ $hideNarrow }) => $hideNarrow && narrowHidden}
  ${typographyMixin("caption", 3)}
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: ${({ $align }) => textAlignByMode[$align]};
  height: 40px;
  padding: ${spacing[3]} ${spacing[5]};
  background-color: ${({ theme }) => theme.colors.primary[100]};
  white-space: nowrap;
  width: ${({ $width }) => $width ?? "auto"};
  /* The compact grey head is for the default look; a plain head keeps its own (below). */
  ${({ $density, $appearance }) => $density === "compact" && $appearance !== "plain" && compactHead}
  ${({ $stickyHeader }) =>
    $stickyHeader
      ? css`
          /* Bottom edge comes from sticky thead box-shadow — avoids double borders + repaint shimmy. */
          border-bottom: none;
        `
      : css`
          border-bottom: 1px solid ${({ theme }) => theme.colors.border.stroke};
        `}
  ${({ $appearance, $stickyHeader }) =>
    $appearance === "plain" &&
    css`
      ${typographyMixin("caption", 2)}
      line-height: ${lineHeights["18"]};
      text-transform: none;
      height: auto;
      padding: ${uiSpacing[2.5]} ${uiSpacing[3.5]};
      background-color: ${({ theme }) => theme.colors.surface.primary};
      ${!$stickyHeader &&
      css`
        border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
      `}
    `}
`;

export const BodyRow = styled.tr<{ $selected?: boolean; $clickable?: boolean; $density?: DataTableDensity }>`
  height: ${({ $density }) => ($density === "compact" ? spacing[10] : "56px")};
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover td {
        background-color: ${({ theme }) => theme.colors.background.tertiary};
      }
    `}

  &:last-of-type td {
    border-bottom: none;
  }

  ${({ $selected }) =>
    $selected &&
    css`
      td {
        background-color: ${({ theme }) => theme.colors.background.tertiary};
        border-top: 1px solid ${({ theme }) => theme.colors.primary[400]};
        border-bottom: 1px solid ${({ theme }) => theme.colors.primary[400]} !important;
      }
    `}
`;

export const BodyCell = styled.td<{
  $align: DataTableAlign;
  $bodyVariant: DataTableBodyVariant;
  $emphasis: DataTableEmphasis;
  $width?: string;
  $noWrap?: boolean;
  $hideNarrow?: boolean;
  $density?: DataTableDensity;
  /** Compact cells under a plain head: 12/14 padding and the column's own type, not the dense-data cell. */
  $plain?: boolean;
}>`
  ${({ $hideNarrow }) => $hideNarrow && narrowHidden}
  ${({ $bodyVariant }) => typographyMixin("body", $bodyVariant)}
  color: ${({ theme, $emphasis }) => emphasisToColor($emphasis, theme)};
  text-align: ${({ $align }) => textAlignByMode[$align]};
  padding: ${({ $density }) => ($density === "compact" ? `${uiSpacing[3]} ${uiSpacing[3.5]}` : `${spacing[3]} ${spacing[5]}`)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.stroke};
  vertical-align: middle;
  width: ${({ $width }) => $width ?? "auto"};
  ${({ $noWrap }) =>
    $noWrap &&
    css`
      white-space: nowrap;
    `}
  ${({ $density, $plain }) => $density === "compact" && !$plain && compactCell}
`;

/**
 * Used for add-row cells only. Sets position: relative so that absolutely
 * positioned children (InlineInput, InlineDropdownWrap) can use inset: 0 to
 * fill the cell edge-to-edge regardless of table height quirks.
 */
export const AddBodyCell = styled(BodyCell)`
  position: relative;
  padding: 0;
`;

/** A sortable header's button: label plus a direction mark, inheriting the header's type and colour. */
export const SortButton = styled.button<{ $active: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  text-transform: inherit;
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : "inherit")};
  cursor: pointer;

  svg {
    width: 0.875rem;
    height: 0.875rem;
    flex: 0 0 auto;
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

/** The checkbox column's body cell: narrower side padding, the box centred. */
export const SelectionCell = styled(BodyCell)`
  padding-left: ${spacing[3]};
  padding-right: ${spacing[3]};
`;

export const LoadMoreFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing[3]} ${spacing[5]};
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;

export const ExpandedCell = styled.td`
  padding: ${spacing[3]} ${spacing[5]};
  background-color: ${({ theme }) => theme.colors.background.tertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;
